import { useState, useEffect } from 'react';
import { useProfileUpdate } from '../../hooks/useProfileUpdate';
import { usePasswordChange } from '../../hooks/usePasswordChange';
import { useIconUpdate } from '../../hooks/useIconUpdate';
import { useAccountDeletion } from '../../hooks/useAccountDeletion';
import { useHospital } from '../../hooks/useHospital';
import { AccountProps } from './types';

/**
 * Accountページ全体のロジックを統合するカスタムフック
 */
export const useAccount = ({ currentUser, setCurrentUser }: AccountProps) => {
  // レスポンシブデザイン用の画面幅状態
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  
  // 画面幅の変更を監視
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 病院情報の取得
  const hospitalHook = useHospital();

  // プロフィール更新フック
  const profileHook = useProfileUpdate({
    currentUser,
    onUserUpdate: setCurrentUser
  });

  // パスワード変更フック
  const passwordHook = usePasswordChange();

  // アイコン更新フック
  const iconHook = useIconUpdate({
    currentUser,
    onUserUpdate: setCurrentUser
  });

  // アカウント削除フック
  const accountDeletionHook = useAccountDeletion();

  return {
    // 画面幅
    windowWidth,

    // プロフィール更新
    profileData: profileHook.profileData,
    profileMessage: profileHook.profileMessage,
    isUpdatingProfile: profileHook.isUpdating,
    profileError: profileHook.error,
    handleProfileChange: profileHook.handleProfileChange,
    handleProfileSubmit: profileHook.handleProfileSubmit,

    // パスワード変更
    passwordData: passwordHook.passwordData,
    passwordMessage: passwordHook.passwordMessage,
    isChangingPassword: passwordHook.isChanging,
    passwordError: passwordHook.error,
    handlePasswordChange: passwordHook.handlePasswordChange,
    handlePasswordSubmit: passwordHook.handlePasswordSubmit,

    // アイコン更新
    iconFile: iconHook.file,
    editorOpen: iconHook.isEditorOpen,
    zoom: iconHook.zoom,
    rotation: iconHook.rotation,
    position: iconHook.position,
    editorRef: iconHook.editorRef,
    imageSize: iconHook.imageSize,
    originalFile: iconHook.originalFile,
    compressedSize: iconHook.compressedSize,
    iconUrl: iconHook.iconUrl,
    isUpdatingIcon: iconHook.isUpdating,
    iconError: iconHook.error,
    handleIconChange: iconHook.handleFileChange,
    handleZoomChange: iconHook.handleZoomChange,
    handleRotationChange: iconHook.handleRotationChange,
    handlePositionChange: iconHook.handlePositionChange,
    handleSaveEdit: iconHook.handleSaveEdit,
    handleCancelEdit: iconHook.cancelEdit,
    handleIconSubmit: iconHook.handleIconSubmit,
    
    // 病院情報
    hospitals: hospitalHook.hospitals,
    isLoadingHospitals: hospitalHook.isLoading,
    getCurrentHospitalName: (hospitalId?: number) => 
      hospitalHook.getHospitalNameById(hospitalId || currentUser.hospital_id),

    // アカウント削除
    showDeleteConfirm: accountDeletionHook.showDeleteConfirm,
    deletingAccount: accountDeletionHook.isDeleting,
    deleteAccountError: accountDeletionHook.error,
    handleDeleteAccount: accountDeletionHook.handleDeleteAccount,
    setShowDeleteConfirm: (show: boolean) => show 
      ? accountDeletionHook.confirmDeletion() 
      : accountDeletionHook.cancelDeletion()
  };
}; 